import React, { Suspense, createContext, lazy, useContext, useEffect, useMemo } from 'react';
import { createBrowserRouter, RouterProvider, createRoutesFromElements, Route, Navigate } from 'react-router-dom';
import { useMediaQuery } from "react-responsive";

import { ErrorBoundary } from "./Common/ErrorBoundary/errorBoundary";
import Error404 from "./Common/Error/error404";
import { SimpleLoader } from "./Common/Loader/simpleLoader";
import { trackPageView } from "../Utils/Helpers/Metrika/metrika";

// Layouts
import { Layout as LayoutAccount } from "./Account/Desktop/Layout/layout";
import { Layout as LayoutSecure } from "./secure/Layout/layout";
import { Layout as LayoutSecureMobile } from "./secure_mobile/Layout/layout";

// Auth
const Auth = lazy(() => import("./Account/Desktop/Content/Auth/auth"));

// Guest components
const GuestContent = lazy(() => import("./secure/Content/content"));
const GuestMobileContent = lazy(() => import("./secure_mobile/Content/content"));
const Reservations = lazy(() => import("./secure/Content/Reservations/reservations"));
const ReservationsMobile = lazy(() => import("./secure_mobile/Content/Reservations/reservations"));
const Review = lazy(() => import("./secure/Content/Reservations/Review/review"));
const ReviewMobile = lazy(() => import("./secure_mobile/Content/Reservations/Review/review"));
const Reviews = lazy(() => import("./secure/Content/Progress/Reviews/reviews"));
const ReviewsMobile = lazy(() => import("./secure_mobile/Content/Progress/Reviews/reviews"));
const Inbox = lazy(() => import("./secure/Content/Inbox/inbox"));
const InboxMobile = lazy(() => import("./secure_mobile/Content/Inbox/inbox"));
const ChatMobile = lazy(() => import("./secure_mobile/Content/Inbox/Chat/chat"));
const ProfileMobile = lazy(() => import("./secure_mobile/Content/Profile/profile"));
const AccountSettings = lazy(() => import("./secure/Content/AccountSettings/accountSettings"));
const AccountSettingsMobile = lazy(() => import("./secure_mobile/Content/AccountSettings/accountSettings"));
const PersonalInfo = lazy(() => import("./secure/Content/AccountSettings/personalInfo/personalInfo"));
const PersonalInfoMobile = lazy(() => import("./secure_mobile/Content/AccountSettings/PersonalInfo/personalInfo"));
const Notifications = lazy(() => import("./secure/Content/AccountSettings/Notifications/notifications"));
const NotificationsMobile = lazy(() => import("./secure_mobile/Content/AccountSettings/Notifications/notifications"));
const DeleteAccount = lazy(() => import("./secure/Content/AccountSettings/DeleteAccount/deleteAccount"));
const EmailVerification = lazy(() => import("./Common/Email/emailVerification"));

const AppPropsContext = createContext(null);

function WithAppProps({ component: Component }) {
    const props = useContext(AppPropsContext);
    return <Component {...props} />;
}

export function App(props) {
    const isDesktop = useMediaQuery({ query: '(min-width: 748px)' })

    const router = useMemo(() => createBrowserRouter(
        createRoutesFromElements(
            <Route>
                <Route path="/login" element={<WithAppProps component={LayoutAccount} />}>
                    <Route index element={<WithAppProps component={Auth} />} />
                    <Route path="sign-up" element={<WithAppProps component={Auth} />} />
                    <Route path="register" element={<WithAppProps component={Auth} />} />
                </Route>
                <Route path="/user/email-verify" element={<WithAppProps component={EmailVerification} />} />
                <Route path="/" element={<WithAppProps component={isDesktop ? LayoutSecure : LayoutSecureMobile} />}>
                    <Route index element={<WithAppProps component={isDesktop ? GuestContent : GuestMobileContent} />} />
                    <Route path="reservations" element={<WithAppProps component={isDesktop ? Reservations : ReservationsMobile} />} />
                    <Route path="reservations/:id/review" element={<WithAppProps component={isDesktop ? Review : ReviewMobile} />} />
                    <Route path="progress/reviews" element={<WithAppProps component={isDesktop ? Reviews : ReviewsMobile} />} />
                    <Route path="progress/reviews/:id" element={<WithAppProps component={isDesktop ? Reviews : ReviewsMobile} />} />
                    <Route path="inbox" element={<WithAppProps component={isDesktop ? Inbox : InboxMobile} />} />
                    <Route path="inbox/:id" element={<WithAppProps component={isDesktop ? Inbox : ChatMobile} />} />
                    <Route path="profile" element={!isDesktop ? <ProfileMobile /> : null} />
                    <Route path="account-settings" element={<WithAppProps component={isDesktop ? AccountSettings : AccountSettingsMobile} />} />
                    <Route path="account-settings/personal-info" element={<WithAppProps component={isDesktop ? PersonalInfo : PersonalInfoMobile} />} />
                    <Route path="account-settings/notifications" element={<WithAppProps component={isDesktop ? Notifications : NotificationsMobile} />} />
                    <Route path="account-settings/delete-account" element={<WithAppProps component={DeleteAccount} />} />
                    <Route path='*' element={<WithAppProps component={Error404} />}/>
                </Route>
            </Route>
        ),
        {
            future: {
                v7_startTransition: true,
                v7_relativeSplatPath: true,
            }
        }
    ), [isDesktop]);

    useEffect(() => {
        trackPageView();
        return router.subscribe(trackPageView);
    }, [router]);

    return (
        <ErrorBoundary>
            <Suspense fallback={<SimpleLoader />}>
                <AppPropsContext.Provider value={props}>
                    <RouterProvider router={router} />
                </AppPropsContext.Provider>
            </Suspense>
        </ErrorBoundary>
    );
}
